import React, { useState } from 'react';
import { SimpleForm, useUpdate, useNotify, useRefresh, useRecordContext } from 'react-admin';
import { Box } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import {
    Resource,
    DEACTIVATION_LABELS,
    NOTIFICATION_OPTIONS_SUCCESS,
    getDeactivationSuccessMessage
} from 'src/constants';
import {
    ShsButton,
    ShsSaveButton,
    ShsDatePicker,
    ShsDeactivateConfirmDialog,
    ShsSuccessNotification
} from 'src/components/common';
import DateTimeUtils from 'src/utils/datetime';

const styles = {
    display: 'inline-block',
    '&.MuiCardContent-root, &.MuiToolbar-root': {
        display: 'inline-block',
        p: 0,
        m: 0,
        mt: 0.4
    },
    '&.MuiToolbar-root .MuiButtonBase-root': {
        mt: '-2px'
    }
};

const Toolbar: React.FC = () => {
    return (
        <Box sx={styles} className="add-child-toolbar">
            <ShsSaveButton label="Деактивувати" sx={{ height: '45px', ml: 2 }} />
        </Box>
    );
};

export const EmploymentEndDate: React.FC<{ index: number }> = ({ index }) => {
    const [newServiceEndDate, setNewServiceEndDate] = useState<boolean>(false);
    const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
    const [pendingData, setPendingData] = useState<any>(null);
    const [update] = useUpdate();
    const refresh = useRefresh();
    const notify = useNotify();
    const record = useRecordContext();

    const employeeFullName = record
        ? `${record.surname || ''} ${record.name || ''} ${record.middleName || ''}`.trim()
        : '';

    const handleConfirm = async () => {
        if (!pendingData) return;

        const employeeCenters = pendingData.employeeCenters.map((center: any) => {
            const centerWithFormattedDateOfEnding =
                center.tempIndex === index
                    ? { ...center, dateOfEnding: DateTimeUtils.getFormattedDate(pendingData.dateOfEnding) }
                    : center;
            delete centerWithFormattedDateOfEnding.tempIndex;
            return centerWithFormattedDateOfEnding;
        });
        const dataToSubmit = { ...pendingData };
        delete dataToSubmit.dateOfEnding;

        const payload = {
            ...dataToSubmit,
            employeeCenters
        };

        await update(
            Resource.EMPLOYEES,
            {
                id: pendingData.id,
                data: payload,
                meta: { useMock: false, method: `update` }
            },
            {
                mutationMode: 'optimistic',
                onSuccess: () => {
                    setNewServiceEndDate(false);
                    setConfirmOpen(false);
                    setPendingData(null);
                    refresh();
                    const deactivationMessage = getDeactivationSuccessMessage(record.name, record.surname);
                    notify(<ShsSuccessNotification message={deactivationMessage} />, {
                        ...NOTIFICATION_OPTIONS_SUCCESS,
                        undoable: true
                    });
                },
                onError: (error: any) => {
                    setNewServiceEndDate(false);
                    setConfirmOpen(false);
                    setPendingData(null);
                    notify(`Error: ${error?.message}`, { type: 'warning', undoable: true });
                }
            }
        );
    };

    const handleSubmit = (data: any) => {
        setPendingData(data);
        setConfirmOpen(true);
    };

    const handleClose = () => {
        setConfirmOpen(false);
        setPendingData(null);
        setNewServiceEndDate(false);
    };

    return (
        <>
            {newServiceEndDate ? (
                <>
                    <SimpleForm sx={styles} toolbar={<Toolbar />} onSubmit={handleSubmit}>
                        <ShsDatePicker source="dateOfEnding" label="" required />
                    </SimpleForm>
                    <ShsDeactivateConfirmDialog
                        open={confirmOpen}
                        onClose={handleClose}
                        onConfirm={handleConfirm}
                        title={DEACTIVATION_LABELS.EMPLOYEE.TITLE}
                        confirmLabel={DEACTIVATION_LABELS.EMPLOYEE.CONFIRM}
                        cancelLabel={DEACTIVATION_LABELS.EMPLOYEE.CANCEL}>
                        Чи впевнені ви що хочете деактивувати {employeeFullName}?
                        <br />
                        <br />
                        {DEACTIVATION_LABELS.EMPLOYEE.BODY_SUFFIX}
                    </ShsDeactivateConfirmDialog>
                </>
            ) : (
                <ShsButton
                    styleType="round"
                    label="Додати"
                    icon={<AddCircleOutlineIcon />}
                    sx={{ height: '24px', border: 'none !important', p: '0 !important', mt: '-5px' }}
                    onClick={() => setNewServiceEndDate(true)}
                />
            )}
        </>
    );
};
